import { format, differenceInDays } from 'date-fns';
import { Building2, AlertCircle, Settings2 } from 'lucide-react';
import { useLanguage } from '../../../../features/language/useLanguage';
import type { CompanySubscriptionRow } from '../../../../services/subscriptionService';
import { effectiveTier } from './companyTier';
import { TierBadge } from './TierBadge';

interface CompaniesTableProps {
  rows: CompanySubscriptionRow[];
  onAdjust: (row: CompanySubscriptionRow) => void;
}

/** The admin companies-subscriptions list table. */
export function CompaniesTable({ rows, onAdjust }: CompaniesTableProps) {
  const { t } = useLanguage();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left">
          <tr>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
              {t('subscription.admin.col.company')}
            </th>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
              {t('subscription.admin.col.tier')}
            </th>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
              {t('subscription.admin.col.expires')}
            </th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const now = new Date();
            const expiresDate = row.expires_at ? new Date(row.expires_at) : null;
            const eff = effectiveTier(row, now);
            const isPaidActive = eff !== 'free';
            const days = expiresDate ? differenceInDays(expiresDate, now) : null;
            const isExpiringSoon = isPaidActive && days !== null && days <= 7;
            return (
              <tr key={row.company_id} data-test-id={`company-row-${row.company_id}`} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="font-medium text-gray-900">{row.company_name}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <TierBadge tier={eff} />
                </td>
                <td className="px-4 py-3">
                  {expiresDate ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 tabular-nums">{format(expiresDate, 'PPP')}</span>
                      {isExpiringSoon && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 px-1.5 py-0.5 text-[11px] font-medium">
                          <AlertCircle className="h-3 w-3" />
                          {days}d
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onAdjust(row)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 px-3 py-1.5 text-xs font-medium transition-colors"
                    data-test-id={`admin-adjust-${row.company_id}`}
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    {t('subscription.admin.adjust_cta')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
